/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import size from 'lodash/size';
import isNil from 'lodash/isNil';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import Hub from '../../shared/entities/hub';
import { LOCAL_STORAGE_TERMINAL_TAGS_KEY_V2, OverlaysContextsEnum } from './reducer';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../components/common/map/GoogleMapSingleton';

const ftvException = (hubsForBeam: Hub[], state: RootState): string[] => {
  if (state.terminals.tailDetailsPanelTerminal) {
    const terminalDetailsHubName: string = state.terminals.tailDetailsPanelTerminal.hubName;
    const terminalDetailsNextHubName: string = state.terminals.tailDetailsPanelTerminal.nextHub;

    const currentHubs = hubsForBeam.filter(
      (hub: Hub) => hub.name === terminalDetailsHubName || hub.name === terminalDetailsNextHubName,
    );
    if (!isNil(currentHubs) && size(currentHubs)) {
      return currentHubs.map((hub: Hub) => hub.id);
    }
  }

  return [];
};

const addHubToMapByBeamId = (beamId: string, mapId: string, state: RootState): any => {
  const hubsForBeam: Hub[] = state.hubs.beamToHubMap[beamId];

  // if there are hubs for this beam id
  if (size(hubsForBeam)) {
    const currentOverlaysContext = state.maps.overlaysCurrentContext;

    // If the current context is the FTV and the add the current terminal hub name
    if (currentOverlaysContext === OverlaysContextsEnum.FTV || currentOverlaysContext === OverlaysContextsEnum.TRANSPORT_MONITORING) {
      return ActionCreators.AddHubsToMap.create({ hubs: ftvException(hubsForBeam, state), mapId: mapId });
    }

    // Else the current context is not the FTV, add all of the hub to the map by passing the array of hub id's to the reducer
    const hubIds: string[] = hubsForBeam.map((hub: Hub) => hub.id);
    return ActionCreators.AddHubsToMap.create({ hubs: hubIds, mapId: mapId });
  }

  // basically a no-op
  return ActionCreators.AddHubsToMap.create({ hubs: [], mapId: mapId });
};

export const addHubToMapByBeamIdEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.AddBeamToMap.type),
    switchMap(
      ($action: any): Observable<Action> =>
        of(addHubToMapByBeamId($action.payload.beamId, $action.payload.mapId, state$.value)),
    ),
  );

// This removes the hubs from the map when a beam is removed from the map (notice that it will not remove hubs that are being used by other beams on the map)
const updateHubsOnMap = (mapId: string, state: RootState): any => {
  const mapFields = state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY];
  const beamsOnMap: {beamId: string, networkType: string, mapType: string}[] = [...mapFields?.overlaysUiState[state.maps.overlaysCurrentContext]?.beamsOnMap || []];

  // at this point we have removed the current beam's hubs from the object
  const hubsToKeep = new Set<string>([]);

  beamsOnMap.forEach((beamOnMap: {beamId: string, networkType: string, mapType: string}) => {
    const hubsForBeam: Hub[] = state.hubs.beamToHubMap[beamOnMap.beamId];

    if (hubsForBeam) {
      const currentOverlaysContext = state.maps.overlaysCurrentContext;

      // If the current context is the FTV and the add the current terminal hub name
      if (currentOverlaysContext === OverlaysContextsEnum.FTV || currentOverlaysContext === OverlaysContextsEnum.TRANSPORT_MONITORING) {
        ftvException(hubsForBeam, state).forEach((hubId: string) => hubsToKeep.add(hubId));
      } else {
        // Else the current context is not the FTV, add all of the hub to the map by passing the array of hub id's to the reducer
        hubsForBeam.forEach((hubForBeam: Hub) => {
          hubsToKeep.add(hubForBeam.id);
        });
      }
    }
  });

  // Set the next hubs on the map to the hubs that don't include any unique hubs for the beam that was just removed
  return ActionCreators.SetHubsOnMap.create({ hubs: Array.from(hubsToKeep), mapId: mapId });
};

export const updateHubsOnMapEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RemoveBeamFromMap.type),
    switchMap(($action: any): Observable<Action> => of(updateHubsOnMap($action.payload.mapId, state$.value))),
  );


  /**
 * Save the terminal tag state to local storage.
 * @param state the current state of the redux store.
 */
const saveTerminalTagState = (state: RootState): any => {
  try {
    const stateToSave = Object.keys(state.maps.mapContext).reduce((accum, key) => {
        accum[key] = {
          [OverlaysContextsEnum.FLEET_VIEW]: {
            terminalTagSize: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FLEET_VIEW].terminalTagSize,
            tailTagsShown: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FLEET_VIEW].tailTagsShown,
          },
          [OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL]: {
            terminalTagSize: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL].terminalTagSize,
            tailTagsShown: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL].tailTagsShown,
          },
          [OverlaysContextsEnum.FTV]: {
            terminalTagSize: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FTV].terminalTagSize,
            tailTagsShown: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FTV].tailTagsShown,
          },
          [OverlaysContextsEnum.FTV_EXPORT]: {
            terminalTagSize: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FTV_EXPORT].terminalTagSize,
            tailTagsShown: state.maps.mapContext[key].overlaysUiState[OverlaysContextsEnum.FTV_EXPORT].tailTagsShown,
          },
        };

      return accum;
    }, {});

    localStorage.setItem(LOCAL_STORAGE_TERMINAL_TAGS_KEY_V2, JSON.stringify(stateToSave));
  } catch (error) {
    return ActionCreators.SaveTerminalTagStateError.create();
  }

  return ActionCreators.SaveTerminalTagStateSuccessful.create();
};

/**
 * Save the terminal tag ui state any time it is changed in the store.
 * @param action$ the actions to look out for any change in the terminal tags ui state.
 * @param state$ the current redux state. Note: this is the state after the action has gone through
 *               the reducer.
 */
 export const setTerminalTagsInLocalStorage = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.SetTerminalTagsSize.type, ActionCreators.SetTailTagsShown.type),
    switchMap(($action: any): Observable<Action> => of(saveTerminalTagState(state$.value))),
  );